import { createFileRoute, Outlet, useBlocker, Link } from '@tanstack/react-router';
import { useWorkflow, WorkflowProvider } from '@/contexts/WorkflowContext';
import { StageTracker } from '@/components/workflow/StageTracker';
import { useEffect, useRef } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
  DialogHeader,
  DialogFooter
} from '@/components/ui/dialog';

function WorkflowGuard() {
  const { data, resetWorkflow } = useWorkflow();

  // ---------------------------------------------------------------------------
  // reportId ref — updated SYNCHRONOUSLY during render so shouldBlockFn
  // always reads the latest value, even if the blocker evaluates before
  // useEffect runs.  This is safe because the ref assignment is idempotent.
  // ---------------------------------------------------------------------------
  const reportIdRef = useRef(data.reportId);
  reportIdRef.current = data.reportId; // sync on every render, no useEffect delay

  // workflowState ref — tracks whether processing has started
  // Used to allow back-navigation to summary before upload begins
  const workflowStateRef = useRef(data.workflowState);
  workflowStateRef.current = data.workflowState;

  // Handle browser refresh / tab close
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (reportIdRef.current) return;
      e.preventDefault();
      e.returnValue = '';
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, []);

  // ---------------------------------------------------------------------------
  // Navigation blocker
  //
  // Rules (simple and exhaustive):
  //
  //   BLOCK when ALL of these are true:
  //     • User is on a workflow/* page
  //     • User is navigating to a non-workflow page
  //     • No report has been generated yet (no reportId)
  //
  //   ALLOW everything else:
  //     • Within-workflow navigation (step ↔ step)   → always allowed
  //     • Post-report navigation (reportId exists)   → always allowed
  //     • workflow/report → /analyses/$id             → allowed (reportId is set
  //       by updateData before navigate is called)
  //
  // Edge cases handled:
  //   • StageTracker clicking between steps     → within-workflow → allowed
  //   • Browser back from /analyses into workflow after completion → reportId
  //     still set → allowed
  //   • Summary ↔ deeper steps before upload   → allowed (allows backtracking)
  //   • Summary ↔ deeper steps after upload    → blocked (workflow started)
  //   • User clicks sidebar/header nav mid-workflow → blocked (no reportId)
  //   • Ref updated synchronously so no useEffect timing gap
  // ---------------------------------------------------------------------------
  // Once the user confirms "Leave", this ref is flipped synchronously so
  // shouldBlockFn returns false on every subsequent evaluation — including
  // re-renders triggered by resetWorkflow() state updates that race with
  // blocker.proceed().
  const confirmedLeaveRef = useRef(false);

  // Track whether the workflow has any user data worth protecting.
  // Updated synchronously so shouldBlockFn always reads the latest value.
  const hasWorkflowDataRef = useRef(false);
  hasWorkflowDataRef.current = !!(
    data.financialData ||
    data.creditReportData ||
    data.bankStatementFindings ||
    data.accountData ||
    data.summaryPreferences ||
    (data.uploadedDocuments && data.uploadedDocuments.length > 0)
  );

  const blocker = useBlocker({
    shouldBlockFn: ({ current, next }) => {
      // User already confirmed "Leave" — never block again this session
      if (confirmedLeaveRef.current) return false;

      // Nothing to protect → never block (covers post-resetWorkflow navigations)
      if (!hasWorkflowDataRef.current && !reportIdRef.current) return false;

      const hasReport = !!reportIdRef.current;
      const isLeavingWorkflow = !next.pathname.startsWith('/workflow');

      const isSummaryPath = (p: string) =>
        p === '/workflow/summary' || p === '/workflow/' || p === '/workflow';
      const isNavigatingToSummary = isSummaryPath(next.pathname);
      const isAlreadyOnSummary = isSummaryPath(current.pathname);

      // Navigating back to summary from a deeper step → only block if batch upload has started
      // Allow backtracking in 'idle' state (before upload), only block after 'processing' starts
      const processingStarted = workflowStateRef.current === 'uploading' ||
                                 workflowStateRef.current === 'processing' ||
                                 workflowStateRef.current === 'processing_remaining' ||
                                 workflowStateRef.current === 'financial_review' ||
                                 workflowStateRef.current === 'metrics_review' ||
                                 workflowStateRef.current === 'generating' ||
                                 workflowStateRef.current === 'complete';
      if (isNavigatingToSummary && !isAlreadyOnSummary && (!!reportIdRef.current || processingStarted)) return true;

      // Within-workflow navigation (step ↔ step, not summary) → never block
      if (!isLeavingWorkflow) return false;

      // Leaving workflow — only block if the report hasn't been generated yet
      return !hasReport;
    },
    withResolver: true,
  });

  const handleConfirmLeave = () => {
    // Flip BEFORE resetWorkflow so shouldBlockFn sees it immediately —
    // prevents the dialog from re-appearing during the state-update
    // re-render that races with blocker.proceed().
    confirmedLeaveRef.current = true;
    resetWorkflow();
    if (blocker.status === 'blocked') {
      blocker.proceed();
    }
  };

  const handleCancelLeave = () => {
    if (blocker.status === 'blocked') {
      blocker.reset();
    }
  };

  // Determine if workflow has already started processing (after batch upload)
  // Only block after 'processing' begins, not in 'idle' or 'uploading' states
  const processingStarted = data.workflowState === 'idle' ||
                            data.workflowState === 'uploading' ||
                            data.workflowState === 'processing' ||
                            data.workflowState === 'processing_remaining' ||
                            data.workflowState === 'financial_review' ||
                            data.workflowState === 'metrics_review' ||
                            data.workflowState === 'generating' ||
                            data.workflowState === 'complete';
  const workflowStarted = !!data.workflowId || processingStarted;
  const dialogMessage = workflowStarted
    ? (
      <>
        Are you sure you want to navigate away from this page? Your workflow has already started processing, and will continue in the background. You can check the status &amp; completion via the{' '}
        <Link to="/analyses" className="text-amber-500 hover:text-amber-400 underline font-semibold" onClick={() => {
          // Cancel the original blocked navigation and let the Link navigate
          // to /analyses. confirmedLeaveRef prevents the blocker from firing
          // again for this new navigation.
          confirmedLeaveRef.current = true;
          resetWorkflow();
          if (blocker.status === 'blocked') {
            blocker.reset();
          }
        }}>
          My Analyses
        </Link>{' '}
        page.
      </>
    )
    : "Are you sure you want to navigate away from this page? Your workflow state and changes won't be saved!";

  return (
    <>
      <main className="relative max-w-7xl mx-auto px-8 py-8">
        <Outlet />
      </main>

      <Dialog
        open={blocker.status === 'blocked'}
        onOpenChange={(open) => {
          if (!open) handleCancelLeave();
        }}
      >
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle className="text-xl">Navigate away?</DialogTitle>
            <DialogDescription className="text-base pt-2">
              {dialogMessage}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-0 pt-6">
            <Button variant="outline" onClick={handleCancelLeave} className="flex-1">
              Stay on Page
            </Button>
            <Button
              variant="destructive"
              onClick={handleConfirmLeave}
              className="flex-1"
            >
              Leave
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export const Route = createFileRoute('/workflow/_layout')({
  component: WorkflowLayout,
});

function WorkflowLayout() {
  return (
    <WorkflowProvider>
      <div className="min-h-screen bg-background gradient-mesh noise">
        {/* Ambient background glow */}
        <div className="fixed inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-gold-500/5 rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-emerald-500/5 rounded-full blur-3xl" />
        </div>

        {/* Stage Tracker - Shows progress through workflow */}
        <StageTracker />

        <WorkflowGuard />
      </div>
    </WorkflowProvider>
  );
}